/**
 * Asset Counts (AST-88, narrowed by AST-99, renamed by AST-120)
 *
 * Read-only modal: manual counts per mapped location (Structures,
 * Project/Laydown Yards, Other), a row total, and a grand total. Locations
 * and columns that are zero everywhere are hidden (AST-99). Entries with no
 * From/To sit in a "Project level" row; with no mapped locations at all,
 * that row is the whole table. Closing writes nothing.
 *
 * AST-120: was "Mat locations" with one column per SUBTYPE. Now one column
 * per MANUAL ASSET (ST124), A->Z, keyed by id so renames relabel (AC-R9);
 * counts saved before manual assets existed keep a column named for their
 * subtype (Laminate Mat), after the asset columns (AC-R5). Column rules:
 * utils/assetCounts. Location and move rules are unchanged -- this still
 * runs through utils/locationCounts, just with a different column key.
 *
 * Columns come back from locationTable as `table.columns` -- the ones that
 * survived hiding. Index row cells against those, or columns and cells will
 * disagree.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Close as CloseIcon, FileDownloadOutlined as DownloadIcon } from '@mui/icons-material';
import { useAppSelector } from '../../store/hooks';
import { fetchAreaElements } from '../../services/areaElementsApi';
import { fetchProjectManualAssets } from '../../services/manualAssetsApi';
import { SECTIONS } from '../../utils/areaElements';
import { locationTable } from '../../utils/locationCounts';
import { assetColumns, assetColumnOf, orphanSubtypeColumns } from '../../utils/assetCounts';
import { toCsv, toXlsxSheet, exportFileName, downloadBlob, SHEET_NAME } from '../../utils/assetCountsExport';

// AC-R8: when columns overflow, Location and Total stay put while the asset
// columns scroll. Opaque backgrounds, or scrolled cells show through.
const SURFACE = '#3A3938'; // the dialog paper (theme background.paper)
const LOCATION_W = 200;
const stickyLeft = { position: 'sticky', left: 0, zIndex: 2, bgcolor: SURFACE, minWidth: LOCATION_W, maxWidth: LOCATION_W };
const stickyRight = { position: 'sticky', right: 0, zIndex: 2, bgcolor: SURFACE, boxShadow: '-1px 0 0 rgba(255,255,255,0.12)' };

// AC-R7: group rows were white (grey.100) with dark text; now a lighter grey
// than the surface, with light text -- Mark's suggested #5A5754.
const GROUP_BG = '#5A5754';
const GROUP_TEXT = '#F2EEEC';

const SectionRow = ({ label, colSpan }) => (
  <TableRow>
    <TableCell
      colSpan={colSpan}
      component="th"
      scope="rowgroup"
      sx={{ position: 'sticky', top: 37, zIndex: 1, bgcolor: GROUP_BG, color: GROUP_TEXT, fontWeight: 700, p: 0, textAlign: 'left' }}
    >
      {/* The label itself sticks left, so it stays readable when the asset
          columns are scrolled sideways (AC-R8). Needs textAlign: left on
          the cell -- a centred th starts the label mid-row, and it then
          slides off with the scroll (checked in a real browser: 281px ->
          81px at scrollLeft 200; pinned at 0px once left-aligned). */}
      <Box component="span" sx={{ position: 'sticky', left: 0, display: 'inline-block', px: 2, py: 0.75 }}>{label}</Box>
    </TableCell>
  </TableRow>
);

const fmt = (n) => (n === 0 ? '0' : n.toLocaleString());

const MatLocationsDialog = ({ open, onClose, projectId, projectName, entries }) => {
  const { assetTypes, assetSubTypes } = useAppSelector((state) => state.asset);
  // EX-9 "Exported ... by <user>"
  const exportedBy = useAppSelector((state) => state.auth?.userAttributes?.email || state.auth?.userAttributes?.name || '');
  const [elements, setElements] = useState({ items: [], loading: false, error: null });

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    setElements({ items: [], loading: true, error: null });
    fetchAreaElements(projectId)
      .then((res) => { if (!cancelled) setElements({ items: res?.items || [], loading: false, error: null }); })
      .catch(() => { if (!cancelled) setElements({ items: [], loading: false, error: "Couldn't load this project's locations, so everything is shown at project level." }); });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const matType = useMemo(() => (assetTypes || []).find((t) => t.name === 'Mat') || null, [assetTypes]);
  const matSubtypes = useMemo(
    () => (matType ? (assetSubTypes || []).filter((s) => s.typeId === matType.id).map((s) => ({ id: s.id, name: s.name })) : []),
    [assetSubTypes, matType]
  );

  // AST-120: the project customer's manual assets, ARCHIVED INCLUDED so an
  // archived asset with counts keeps its column (AC-R6). Fetched each time
  // the modal opens, so a rename made since shows straight away (AC-R9).
  // Non-fatal: on failure every count falls back to its subtype column.
  const [manualAssets, setManualAssets] = useState([]);
  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    fetchProjectManualAssets(projectId)
      .then((res) => { if (!cancelled) setManualAssets(res?.items || []); })
      .catch(() => { if (!cancelled) setManualAssets([]); });
    return () => { cancelled = true; };
  }, [open, projectId]);

  const assetById = useMemo(() => Object.fromEntries(manualAssets.map((a) => [a.id, a])), [manualAssets]);
  const candidateColumns = useMemo(() => [
    ...assetColumns(manualAssets, matSubtypes, matType?.id),
    ...orphanSubtypeColumns(entries || [], assetById, matSubtypes),
  ], [manualAssets, matSubtypes, matType, entries, assetById]);

  const table = useMemo(
    () => locationTable(entries || [], elements.items, candidateColumns, SECTIONS, assetColumnOf(assetById)),
    [entries, elements.items, candidateColumns, assetById]
  );
  // AST-99: the columns that survived hiding, not every candidate.
  const columns = table.columns;
  const colSpan = columns.length + 2;

  // AST-122: Export. Built from `table` -- the exact on-screen table (EX-3)
  // -- so it is declared after it (a const used before its declaration
  // blanked every project page in #201).
  const exportAnchor = useRef(null);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState(false);
  useEffect(() => { if (!open) { setExportMenuOpen(false); setExportError(false); } }, [open]);
  const canExport = !elements.loading && !!matType && columns.length > 0;

  const runExport = async (kind) => {
    setExportMenuOpen(false);
    setExporting(true);
    setExportError(false);
    try {
      const now = new Date();
      if (kind === 'csv') {
        // EX-8: the BOM is in the string; UTF-8 is the Blob default.
        downloadBlob(new Blob([toCsv(table)], { type: 'text/csv;charset=utf-8' }), exportFileName(projectName, 'csv', now));
      } else {
        // Loaded on demand -- an export library has no business in the main
        // bundle for a button most sessions never press.
        const { default: writeXlsxFile } = await import('write-excel-file/browser');
        const { data, stickyRowsCount, columns: widths } = toXlsxSheet(table, { projectName, user: exportedBy, now });
        const blob = await writeXlsxFile(data, { sheet: SHEET_NAME, stickyRowsCount, columns: widths }).toBlob();
        downloadBlob(blob, exportFileName(projectName, 'xlsx', now));
      }
    } catch (err) {
      console.error('[AST-122] Asset Counts export failed', err);
      setExportError(true); // EX-11 / AC-6
    } finally {
      setExporting(false);
    }
  };

  const dataRow = (r, bold = false) => (
    <TableRow key={r.id} hover>
      <TableCell component="th" scope="row" sx={{ ...stickyLeft, fontWeight: bold ? 700 : 400 }}>{r.name}</TableCell>
      {r.cells.map((n, i) => (
        <TableCell key={columns[i].id} align="right" sx={{ color: n === 0 ? 'text.disabled' : n < 0 ? 'error.main' : 'text.primary', whiteSpace: 'nowrap' }}>
          {fmt(n)}
        </TableCell>
      ))}
      <TableCell align="right" sx={{ ...stickyRight, fontWeight: 700, color: r.total < 0 ? 'error.main' : 'text.primary' }}>{fmt(r.total)}</TableCell>
    </TableRow>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth aria-labelledby="asset-counts-title">
      <DialogTitle id="asset-counts-title" sx={{ pr: 6 }}>
        Asset Counts
        <Typography variant="body2" color="text.secondary">Manually counted assets by location</Typography>
        <IconButton aria-label="Close asset counts" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {elements.error && <Alert severity="warning" sx={{ m: 2 }}>{elements.error}</Alert>}
        {elements.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress aria-label="Loading asset counts" /></Box>
        ) : !matType ? (
          <Typography sx={{ p: 3 }} color="text.secondary">No mat types are set up yet.</Typography>
        ) : columns.length === 0 ? (
          // AST-99: hiding empty rows and columns can empty the table
          // entirely (a project with no counts). Say so, rather than
          // rendering a Location/Total header over a single zero row.
          <Typography sx={{ p: 3 }} color="text.secondary">No assets have been counted on this project yet.</Typography>
        ) : (
          // AC-R8: overflowX lets wide tables scroll sideways; Location and
          // Total are position: sticky inside it.
          <TableContainer sx={{ maxHeight: '65vh', overflowX: 'auto' }}>
            <Table stickyHeader size="small" aria-label="Manually counted assets by location">
              <TableHead>
                <TableRow>
                  {/* Header corners sit above both the sticky header row and
                      the sticky columns. */}
                  <TableCell sx={{ ...stickyLeft, zIndex: 3, fontWeight: 700 }}>Location</TableCell>
                  {columns.map((t) => (
                    <TableCell key={t.id} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{t.name}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ ...stickyRight, zIndex: 3, fontWeight: 700 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {table.groups.map((g) => (
                  <React.Fragment key={g.key}>
                    <SectionRow label={g.label} colSpan={colSpan} />
                    {g.rows.map((r) => dataRow(r))}
                  </React.Fragment>
                ))}
                {table.project && (
                  <>
                    {table.groups.length > 0 && <SectionRow label="Not at a mapped location" colSpan={colSpan} />}
                    {dataRow(table.project)}
                  </>
                )}
                <TableRow sx={{ '& > *': { borderTop: 2, borderColor: 'divider' } }}>
                  <TableCell component="th" scope="row" sx={{ ...stickyLeft, fontWeight: 700 }}>Project total</TableCell>
                  {table.grandTotal.cells.map((n, i) => (
                    <TableCell key={columns[i].id} align="right" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(n)}</TableCell>
                  ))}
                  <TableCell align="right" sx={{ ...stickyRight, fontWeight: 700 }}>{fmt(table.grandTotal.total)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        {/* EX-11: inline in the footer, cleared on the next attempt. */}
        {exportError && (
          <Typography role="alert" variant="body2" color="error" sx={{ mr: 'auto', pl: 1 }}>
            Export failed. Try again.
          </Typography>
        )}
        {/* EX-1: left of Close, outlined, download icon. Only once there's a
            table to export -- the file is built from what's on screen. */}
        {canExport && (
          <>
            <Button
              ref={exportAnchor}
              variant="outlined"
              startIcon={exporting ? <CircularProgress size={16} aria-label="Exporting" /> : <DownloadIcon />}
              onClick={() => setExportMenuOpen(true)}
              disabled={exporting}
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen ? 'true' : undefined}
              aria-controls={exportMenuOpen ? 'asset-counts-export-menu' : undefined}
              sx={{ textTransform: 'none' }}
            >
              Export
            </Button>
            {/* EX-2: CSV or Excel, then it downloads -- no further dialog. */}
            <Menu
              id="asset-counts-export-menu"
              anchorEl={exportAnchor.current}
              open={exportMenuOpen}
              onClose={() => setExportMenuOpen(false)}
              anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
              transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <MenuItem onClick={() => runExport('csv')}><ListItemText>CSV (.csv)</ListItemText></MenuItem>
              <MenuItem onClick={() => runExport('xlsx')}><ListItemText>Excel (.xlsx)</ListItemText></MenuItem>
            </Menu>
          </>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MatLocationsDialog;
